import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { pick, precisionOf, starsOf, useCountdown, TimerBar, Hud, BigButton, Flash, PRACTICE_SECONDS, FLASH_MS } from "./nqShared";

const CFG = {
  1: { seconds: 45, every: 1400, show: 1000, pGo: 0.75, distractors: [], invert: false },
  2: { seconds: 55, every: 1100, show: 800, pGo: 0.7, distractors: ["🟡", "🟣"], invert: false },
  3: { seconds: 70, every: 1000, show: 700, pGo: 0.65, distractors: ["🟡", "🟣"], invert: true },
};

export const brief = (level) => {
  const c = CFG[level.n];
  return {
    title: "Le bouclier arrête la réponse automatique",
    goal: "appuyer sur le bouclier seulement au bon moment.",
    regarde: "Une pastille apparaît au milieu de l'écran : 🟢 ou 🔴.",
    fais: "🟢 = GO : appuie vite sur 🛡️ BOUCLIER (ou sur la barre d'espace) !",
    attention: `🔴 = STOP : ne touche à rien, même si tes doigts ont envie !${c.distractors.length ? " Les autres couleurs sont des pièges : on ne les touche pas." : ""}${c.invert ? " Au milieu de la mission, la règle s'inverse : 🔴 devient GO et 🟢 devient STOP !" : ""}`,
    preview: { ok: ["🟢"], ko: ["🔴", ...c.distractors], okLabel: "GO : j'appuie", koLabel: "STOP : je ne touche pas" },
  };
};

export default function GameStop({ power, level, onFinish, practice, onPracticeEnd }) {
  const cfg = useMemo(() => (practice ? { ...CFG[level.n], seconds: PRACTICE_SECONDS, invert: false } : CFG[level.n]), [level, practice]);
  const [phase, setPhase] = useState("play");
  const [current, setCurrent] = useState(null);
  const [inverted, setInverted] = useState(false);
  const [stats, setStats] = useState({ go: 0, goHit: 0, stopFail: 0, distractHit: 0, stops: 0 });
  const [flash, setFlash] = useState(null);
  const answered = useRef(false);
  const invertedRef = useRef(false);
  const running = phase === "play";

  const finish = useCallback(() => {
    if (practice) return onPracticeEnd();
    const total = stats.go + stats.stops;
    const hits = stats.goHit + (stats.stops - stats.stopFail);
    const precision = precisionOf(hits, total, stats.distractHit);
    return onFinish({ hits, total, errors: stats.stopFail + stats.distractHit, precision, stars: starsOf(precision), detail: `${stats.goHit}/${stats.go} GO réussis · ${stats.stops - stats.stopFail}/${stats.stops} STOP retenus · ${stats.distractHit} piège(s) touché(s)` });
  }, [stats, onFinish, practice, onPracticeEnd]);
  const left = useCountdown(cfg.seconds, running, finish);

  useEffect(() => {
    if (!running || !cfg.invert) return undefined;
    const id = setTimeout(() => { invertedRef.current = true; setInverted(true); setPhase("switch"); setTimeout(() => setPhase("play"), 2000); }, (cfg.seconds / 2) * 1000);
    return () => clearTimeout(id);
  }, [running, cfg]);

  useEffect(() => {
    if (!running) return undefined;
    const tick = () => {
      const r = Math.random();
      let kind = r < cfg.pGo ? "go" : "stop";
      if (cfg.distractors.length && r > 0.9) kind = "distract";
      const goEmoji = invertedRef.current ? "🔴" : "🟢";
      const stopEmoji = invertedRef.current ? "🟢" : "🔴";
      const emoji = kind === "go" ? goEmoji : kind === "stop" ? stopEmoji : pick(cfg.distractors);
      answered.current = false;
      setCurrent({ kind, emoji, key: Date.now() });
      setStats((s) => ({ ...s, go: s.go + (kind === "go" ? 1 : 0), stops: s.stops + (kind === "stop" ? 1 : 0) }));
      setTimeout(() => setCurrent(null), cfg.show);
    };
    tick();
    const id = setInterval(tick, cfg.every);
    return () => clearInterval(id);
  }, [running, cfg]);

  const press = () => {
    if (!running || answered.current || !current) return;
    answered.current = true;
    if (current.kind === "go") { setStats((s) => ({ ...s, goHit: s.goHit + 1 })); setFlash("ok"); }
    else if (current.kind === "stop") { setStats((s) => ({ ...s, stopFail: s.stopFail + 1 })); setFlash("ko"); }
    else { setStats((s) => ({ ...s, distractHit: s.distractHit + 1 })); setFlash("ko"); }
    setTimeout(() => setFlash(null), FLASH_MS);
  };

  const goLabel = inverted ? "🔴 = GO" : "🟢 = GO";
  return (
    <div className="space-y-4" data-testid="nq-game-stop">
      <TimerBar left={left} total={cfg.seconds} color={power.color} />
      <Hud items={[["Règle :", goLabel, inverted ? "#8B6FC0" : power.color], ["GO réussis", `${stats.goHit}/${stats.go}`, "#7CC6A6"], ["STOP ratés", stats.stopFail, "#E0705C"]]} />
      <div className="relative rounded-3xl bg-[#FDE4DF] p-6 flex flex-col items-center gap-6">
        {phase === "switch" && (
          <div className="absolute inset-0 z-10 rounded-3xl bg-[#ECE6F7]/95 flex flex-col items-center justify-center text-center p-6 animate-fade-up" data-testid="nq-stop-switch">
            <div className="text-5xl mb-2">🔄</div>
            <div className="font-serif-title text-2xl text-[#5E4A9C]">Règle inversée !</div>
            <p className="text-[var(--ink)] mt-1">Maintenant <strong>🔴 = GO</strong> et <strong>🟢 = STOP</strong>.</p>
          </div>
        )}
        <Flash kind={flash} koLabel="✗ Il fallait attendre !" />
        <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-3xl bg-white border-4 flex items-center justify-center text-7xl sm:text-8xl shadow-inner" style={{ borderColor: power.color }} data-testid="nq-stop-screen">
          {current ? <span key={current.key} data-testid={`nq-stop-${current.kind}`}>{current.emoji}</span> : <span className="text-3xl text-[var(--beige)]">·</span>}
        </div>
        <BigButton onClick={press} color={power.color} testid="nq-stop-button">🛡️ BOUCLIER</BigButton>
        <p className="text-xs text-[var(--ink-soft)]">Astuce : tu peux aussi appuyer sur la barre d'espace.</p>
      </div>
      <KeyPress onPress={press} />
    </div>
  );
}

const KeyPress = ({ onPress }) => {
  useEffect(() => { const h = (e) => { if (e.code === "Space") { e.preventDefault(); onPress(); } }; window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onPress]);
  return null;
};
